import { parseWithZod } from '@conform-to/zod'
import {
  ActionFunctionArgs,
  json,
  LoaderFunction,
  MetaFunction,
  redirect,
} from '@remix-run/node'
import { useLoaderData } from '@remix-run/react'
import { z } from 'zod'

import {
  MapPage,
  partialAreasMultiSelectFormSchema,
  partialFacilitiesSchemaSchema,
  partialKeywordsSchema,
  partialMonthlyCostsFormSchema,
  partialNumberOfUsersMultiSelectFormSchema,
  partialOfficeTypeMultiSelectFormSchema,
} from '~/features'
import { drawerSearchFormSchema } from '~/features/map-page/components/drawer-search-result/drawer-seach-form'
import { isSpDevice, isTabletDevice } from '~/lib/user-agent'
import { getAllFacilities, getAllTypes } from '~/models/master.server'
import { searchOffice, SearchOfficeConditions } from '~/models/office.server'
import { Prefecture } from '~/models/types'

export const meta: MetaFunction = ({ matches }) => {
  const parentMeta = matches
    .flatMap((match) => match.meta ?? [])
    .filter((meta) => !('title' in meta))
  return [
    ...parentMeta,
    { title: '検索結果 | シェアオフィス コワーキングスペース検索サイト' },
  ]
}

export const searchQuerySchema = z
  .object({
    sortOrder: z.string().optional(),
    centerLatitude: z.coerce.number().optional(),
    centerLongitude: z.coerce.number().optional(),
    centerRadius: z.coerce.number().optional(),
    prefectureSlug: z.string().optional(),
    initialPinnedOfficeId: z.string().optional(),
  })
  .merge(partialKeywordsSchema)
  .merge(partialAreasMultiSelectFormSchema)
  .merge(partialOfficeTypeMultiSelectFormSchema)
  .merge(partialNumberOfUsersMultiSelectFormSchema)
  .merge(partialFacilitiesSchemaSchema)
  .merge(partialMonthlyCostsFormSchema)

const parseParams = (searchParams: URLSearchParams) => {
  const multiSelectKeys = [
    ...Object.keys(partialAreasMultiSelectFormSchema.shape),
    ...Object.keys(partialOfficeTypeMultiSelectFormSchema.shape),
    ...Object.keys(partialNumberOfUsersMultiSelectFormSchema.shape),
    ...Object.keys(partialFacilitiesSchemaSchema.shape),
  ]

  const transformedParams = Object.fromEntries(
    Array.from(searchParams.entries()).map(([key, value]) => {
      if (multiSelectKeys.includes(key)) {
        return [key, value.includes(',') ? value.split(',') : [value]]
      }

      return [key, value]
    }),
  )

  const parsedSearchParams = searchQuerySchema.safeParse(transformedParams)

  return { parsedSearchParams }
}

const createSortOrderCondition = (
  sortOrder: z.infer<typeof searchQuerySchema>['sortOrder'],
): SearchOfficeConditions['sortOrder'] => {
  switch (sortOrder) {
    case 'registerDateAsc':
      return 'NEWEST'
    case 'monthlyRentAsc':
      return 'CHEAPEST'
    case 'sizeDesc':
      return 'LARGEST'
    case 'updateDateDesc':
      return 'RECENTLY_UPDATED'
    default:
      return undefined
  }
}

// NOTE: 1920×1080の画面サイズで表示される範囲の半径
const DEFAULT_RADIUS_PC = 136
// NOTE: 768×1024の画面サイズで表示される範囲の半径
const DEFAULT_RADIUS_TABLET = 79
// NOTE: 390×844の画面サイズで表示される範囲の半径
const DEFAULT_RADIUS_SP = 57.5

const getDefaultRadiusByDevice = (userAgent: string) => {
  return isSpDevice(userAgent)
    ? DEFAULT_RADIUS_SP
    : isTabletDevice(userAgent)
      ? DEFAULT_RADIUS_TABLET
      : DEFAULT_RADIUS_PC
}

const OFFICE_COUNT_LIMIT = 50
const DEFAULT_CENTER = { lat: 35.6812362, lng: 139.7649361 }

// todo: 今は東京のみなのでこうしている。将来的にDBから取得するようにするかgoogleMapAPIで都道府県の中心の座標を取得する
const MOCK_CENTER_LOCATIONS: Array<
  Prefecture & {
    lat: number
    lng: number
  }
> = [
  { id: 1, name: '北海道', slug: 'hokkaido', lat: 43.06417, lng: 141.34694 },
  { id: 2, name: '青森県', slug: 'aomori', lat: 40.82444, lng: 140.74 },
  { id: 3, name: '岩手県', slug: 'iwate', lat: 39.70361, lng: 141.1525 },
  { id: 4, name: '宮城県', slug: 'miyagi', lat: 38.26889, lng: 140.87194 },
  { id: 5, name: '秋田県', slug: 'akita', lat: 39.71861, lng: 140.1025 },
  { id: 6, name: '山形県', slug: 'yamagata', lat: 38.24056, lng: 140.36333 },
  { id: 7, name: '福島県', slug: 'fukushima', lat: 37.75, lng: 140.46778 },
  { id: 8, name: '茨城県', slug: 'ibaraki', lat: 36.34139, lng: 140.44667 },
  { id: 9, name: '栃木県', slug: 'tochigi', lat: 36.56583, lng: 139.88361 },
  { id: 10, name: '群馬県', slug: 'gunma', lat: 36.39111, lng: 139.06083 },
  { id: 11, name: '埼玉県', slug: 'saitama', lat: 35.85694, lng: 139.64889 },
  { id: 12, name: '千葉県', slug: 'chiba', lat: 35.60472, lng: 140.12333 },
  { id: 13, name: '東京都', slug: 'tokyo', lat: 35.6812362, lng: 139.7649361 },
  { id: 14, name: '神奈川県', slug: 'kanagawa', lat: 35.44778, lng: 139.6425 },
  { id: 15, name: '新潟県', slug: 'niigata', lat: 37.90222, lng: 139.02361 },
  { id: 16, name: '富山県', slug: 'toyama', lat: 36.69528, lng: 137.21139 },
  { id: 17, name: '石川県', slug: 'ishikawa', lat: 36.59444, lng: 136.62556 },
  { id: 18, name: '福井県', slug: 'fukui', lat: 36.06528, lng: 136.22194 },
  { id: 19, name: '山梨県', slug: 'yamanashi', lat: 35.66389, lng: 138.56833 },
  { id: 20, name: '長野県', slug: 'nagano', lat: 36.65139, lng: 138.18111 },
  { id: 21, name: '岐阜県', slug: 'gifu', lat: 35.39111, lng: 136.72222 },
  { id: 22, name: '静岡県', slug: 'shizuoka', lat: 34.97694, lng: 138.38306 },
  { id: 23, name: '愛知県', slug: 'aichi', lat: 35.18028, lng: 136.90667 },
  { id: 24, name: '三重県', slug: 'mie', lat: 34.73028, lng: 136.50861 },
  { id: 25, name: '滋賀県', slug: 'shiga', lat: 35.00444, lng: 135.86833 },
  { id: 26, name: '京都府', slug: 'kyoto', lat: 35.02139, lng: 135.75556 },
  { id: 27, name: '大阪府', slug: 'osaka', lat: 34.68639, lng: 135.52 },
  { id: 28, name: '兵庫県', slug: 'hyogo', lat: 34.69139, lng: 135.18306 },
  { id: 29, name: '奈良県', slug: 'nara', lat: 34.68528, lng: 135.83278 },
  { id: 30, name: '和歌山県', slug: 'wakayama', lat: 34.22611, lng: 135.1675 },
  { id: 31, name: '鳥取県', slug: 'tottori', lat: 35.50361, lng: 134.23833 },
  { id: 32, name: '島根県', slug: 'shimane', lat: 35.47222, lng: 133.05056 },
  { id: 33, name: '岡山県', slug: 'okayama', lat: 34.66167, lng: 133.935 },
  { id: 34, name: '広島県', slug: 'hiroshima', lat: 34.39639, lng: 132.45944 },
  { id: 35, name: '山口県', slug: 'yamaguchi', lat: 34.18611, lng: 131.47139 },
  { id: 36, name: '徳島県', slug: 'tokushima', lat: 34.06583, lng: 134.55944 },
  { id: 37, name: '香川県', slug: 'kagawa', lat: 34.34028, lng: 134.04333 },
  { id: 38, name: '愛媛県', slug: 'ehime', lat: 33.84167, lng: 132.76611 },
  { id: 39, name: '高知県', slug: 'kochi', lat: 33.55972, lng: 133.53111 },
  { id: 40, name: '福岡県', slug: 'fukuoka', lat: 33.60639, lng: 130.41806 },
  { id: 41, name: '佐賀県', slug: 'saga', lat: 33.24944, lng: 130.29889 },
  { id: 42, name: '長崎県', slug: 'nagasaki', lat: 32.74472, lng: 129.87361 },
  { id: 43, name: '熊本県', slug: 'kumamoto', lat: 32.78972, lng: 130.74167 },
  { id: 44, name: '大分県', slug: 'oita', lat: 33.23806, lng: 131.6125 },
  { id: 45, name: '宮崎県', slug: 'miyazaki', lat: 31.91111, lng: 131.42389 },
  {
    id: 46,
    name: '鹿児島県',
    slug: 'kagoshima',
    lat: 31.56028,
    lng: 130.55806,
  },
  { id: 47, name: '沖縄県', slug: 'okinawa', lat: 26.2125, lng: 127.68111 },
]
const getCenterLocationByPrefecture = (prefectureSlug: string) => {
  const prefectureCenterLocation = MOCK_CENTER_LOCATIONS.find(
    (location) => location.slug === prefectureSlug,
  )

  // 指定したprefectureやその中心座標が取得できない場合はデフォルトの東京の座標を返す
  if (
    !prefectureCenterLocation ||
    !prefectureCenterLocation?.lat ||
    !prefectureCenterLocation?.lng
  ) {
    return {
      centerLatitude: DEFAULT_CENTER.lat,
      centerLongitude: DEFAULT_CENTER.lng,
    }
  }

  return {
    centerLatitude: prefectureCenterLocation.lat,
    centerLongitude: prefectureCenterLocation.lng,
  }
}
const getCenterLocation = (searchParams: z.infer<typeof searchQuerySchema>) => {
  // centerLatitudeとcenterLongitudeの指定があればその座標を返す
  if (searchParams.centerLatitude && searchParams.centerLongitude) {
    return {
      centerLatitude: searchParams.centerLatitude,
      centerLongitude: searchParams.centerLongitude,
    }
  }

  // prefectureSlugの指定があればその県の中心座標を返す
  if (searchParams.prefectureSlug) {
    return getCenterLocationByPrefecture(searchParams.prefectureSlug)
  }

  // どちらも指定がない場合はデフォルトの東京の座標を返す
  return {
    centerLatitude: DEFAULT_CENTER.lat,
    centerLongitude: DEFAULT_CENTER.lng,
  }
}

const getSearchResults = async (
  searchParams: z.infer<typeof searchQuerySchema>,
  defaultRadiusByDevice: number,
) => {
  const searchConditions: SearchOfficeConditions = {
    areaIds: searchParams.areas?.map(Number) ?? [],
    typeIds: searchParams.officeTypes?.map(Number) ?? [],
    facilityIds: searchParams.facilities?.map(Number) ?? [],
    minPeople: searchParams.minPeople,
    maxMonthlyRent: searchParams.maxMonthlyRent,
    minMonthlyRent: searchParams.minMonthlyRent,
    freeWord: searchParams.keyword,
    prefectureSlug: searchParams.prefectureSlug,
    centerLatitude: getCenterLocation(searchParams).centerLatitude,
    centerLongitude: getCenterLocation(searchParams).centerLongitude,
    centerRadius: searchParams.centerRadius ?? defaultRadiusByDevice,
    sortOrder: createSortOrderCondition(searchParams.sortOrder),
    take: OFFICE_COUNT_LIMIT,
  }

  // nullとundefinedはパラメータに含めない
  const filteredParams = Object.fromEntries(
    Object.entries(searchConditions).filter(
      (
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        [key, value],
      ) => value != null,
    ),
  )

  return await searchOffice(filteredParams)
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData()

  const submission = parseWithZod(formData, { schema: drawerSearchFormSchema })

  if (submission.status !== 'success') {
    return submission.reply()
  }

  const queryParams = new URLSearchParams()

  formData.forEach((value, key) => {
    if (key !== 'prefectureSlug' && value.toString().trim() !== '') {
      if (queryParams.has(key)) {
        const existingValue = queryParams.get(key)
        queryParams.set(key, `${existingValue},${value.toString()}`)
      } else {
        queryParams.set(key, value.toString())
      }
    }
  })

  const searchUrl = `/map?${queryParams.toString()}`

  return redirect(searchUrl)
}

export const loader: LoaderFunction = async ({ request }) => {
  const { searchParams } = new URL(request.url)

  const { parsedSearchParams } = parseParams(searchParams)

  if (!parsedSearchParams.success) {
    throw new Response('Invalid search parameters', { status: 400 })
  }

  const userAgent = request.headers.get('user-agent') || ''

  const { offices, count } = await getSearchResults(
    parsedSearchParams.data,
    getDefaultRadiusByDevice(userAgent),
  )

  const parsedParamsData = parsedSearchParams.data
  const initialFormValues = {
    officeTypes: parsedParamsData.officeTypes || [],
    facilities: parsedParamsData.facilities || [],
    minPeople: parsedParamsData.minPeople?.toString() || '',
    minMonthlyRent: parsedParamsData.minMonthlyRent?.toString() || '',
    maxMonthlyRent: parsedParamsData.maxMonthlyRent?.toString() || '',
    keyword: parsedParamsData.keyword || '',
  }

  const initialMapCenterLocation = {
    lat: getCenterLocation(parsedParamsData).centerLatitude,
    lng: getCenterLocation(parsedParamsData).centerLongitude,
    radius:
      parsedParamsData.centerRadius || getDefaultRadiusByDevice(userAgent),
  }
  const [allTypes, allFacilities] = await Promise.all([
    getAllTypes(),
    getAllFacilities(),
  ])

  // NOTE: オフィス詳細画面から遷移してきた場合はその対象のオフィスを初期値として設定する
  const initialSelectedOffice =
    offices.find(
      (office) => office.id === Number(parsedParamsData.initialPinnedOfficeId),
    ) ?? null

  return json({
    offices,
    totalResultCount: count,
    initialMapCenterLocation,
    initialFormValues,
    allTypes,
    allFacilities,
    initialSelectedOffice,
  })
}

export default function Map() {
  const {
    offices,
    totalResultCount,
    initialMapCenterLocation,
    initialFormValues,
    allTypes,
    allFacilities,
    initialSelectedOffice,
  } = useLoaderData<typeof loader>()

  return (
    <MapPage
      facilities={allFacilities}
      initialFormValues={initialFormValues}
      initialMapCenterLocation={initialMapCenterLocation}
      initialSelectedOffice={initialSelectedOffice}
      officeTypes={allTypes}
      offices={offices}
      totalResultCount={totalResultCount}
    />
  )
}
